import { colors } from '@kmns-internal/furikake/tokens'
import { FilterAlt, LocationOn } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { useSelectablePlaceNodes } from 'src/features/scheduledReportSummary/hooks/useSelectablePlaceNodes'
import type {
  ScheduledReportSummaryFilters,
  useFilters,
} from 'src/features/scheduledReportSummary/useFilters'

type Props = {
  filters: ScheduledReportSummaryFilters
  isSomeFilterActive: boolean
  resetFilterOf: ReturnType<typeof useFilters>['resetFilterOf']
  setFilterOf: ReturnType<typeof useFilters>['setFilterOf']
}

export const FiltersPopover = ({
  filters,
  isSomeFilterActive,
  resetFilterOf,
  setFilterOf,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isOpen = anchorEl !== null

  const {
    isError,
    isPending,
    placeNodes: selectablePlaceNodes,
  } = useSelectablePlaceNodes()

  return (
    <>
      <Button
        color={isSomeFilterActive ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        絞り込み: {isSomeFilterActive ? 'ON' : 'OFF'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { width: 400, p: 2 } },
        }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => resetFilterOf.allExceptInDrawer()}>
              クリア
            </Button>
          </Box>
          {isError ? (
            <ErrorMessage noMargin>エラーが発生しました</ErrorMessage>
          ) : (
            <Stack spacing={1}>
              <Typography variant="subtitle1">現場</Typography>
              <Autocomplete
                multiple
                size="small"
                loading={isPending}
                options={selectablePlaceNodes}
                getOptionLabel={option => option.name}
                value={selectablePlaceNodes.filter(node =>
                  filters.placeNodeUuids.includes(node.uuid),
                )}
                isOptionEqualToValue={(option, value) =>
                  option.uuid === value.uuid
                }
                onChange={(_, newValue) =>
                  setFilterOf.placeNodeUuids(newValue.map(n => n.uuid))
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.uuid}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{ display: 'flex', flexShrink: 0, minWidth: 16 }}
                      >
                        {option.type === 'placeGroup' ? (
                          <PlaceGroupSVG
                            style={{ width: 16, color: colors.action.active }}
                          />
                        ) : (
                          <LocationOn
                            sx={{ width: 16, color: colors.action.active }}
                          />
                        )}
                      </Box>
                      {option.name}
                    </Stack>
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
                slotProps={{
                  chip: {
                    size: 'small',
                    variant: 'pale',
                  },
                }}
              />
            </Stack>
          )}
        </Stack>
      </Popover>
    </>
  )
}
